'use client'
import { apiSubscribeNewsletter } from '@/apis/page_optimization'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Send, CheckCircle, PawPrint } from 'lucide-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { apiVerifyCaptcha } from '@/apis/captcha'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', YELLOW: '#FF9934', BROWN: '#8C5826', TEXT: '#1a1a1a', MUTED: '#6b7280', BORDER: '#ffe0c2' }

const FooterNewsletterInner = ({ companyId, domain }: { companyId: string; domain?: string }) => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return regex.test(emailStr)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 })
            return
        }

        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.', { autoClose: 2000 })
            return
        }

        setLoading(true)

        try {
            // Step 1: Verify token using domain if recaptcha is available
            if (executeRecaptcha && domain) {
                try {
                    const recaptchaToken = await executeRecaptcha('newsletter_form')
                    if (recaptchaToken) {
                        const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken)
                        if (captchaRes && captchaRes.verified === false) {
                            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 })
                            setLoading(false)
                            return
                        }
                    }
                } catch (recaptchaErr) {
                    console.warn('reCAPTCHA skipped or failed:', recaptchaErr)
                }
            }

            // Step 2: Subscribe newsletter using companyId
            const response = await apiSubscribeNewsletter(companyId, email)
            const resMsg = typeof response?.message === 'string' ? response.message.trim() : ''
            const resStatus: any = response?.status

            const isSuccess =
                (
                    resStatus === true ||
                    resStatus === 'true' ||
                    resStatus === 200 ||
                    resStatus === '200' ||
                    resStatus === 201 ||
                    resStatus === '201' ||
                    (response as any)?.success === true ||
                    resMsg.toLowerCase().includes('subscribed successfully') ||
                    resMsg.toLowerCase().includes('subscriber successfully added') ||
                    resMsg.toLowerCase().includes('thank you') ||
                    resMsg.toLowerCase().includes('success') ||
                    resMsg.toLowerCase().includes('already') ||
                    resMsg.toLowerCase().includes('exists') ||
                    resMsg.toLowerCase().includes('taken')
                ) &&
                !resMsg.includes('Server responded with status') &&
                !resMsg.toLowerCase().includes('fail') &&
                !resMsg.toLowerCase().includes('not found')

            if (isSuccess) {
                toast.success('Thank you for subscribing!', { autoClose: 2500 })
                setEmail('')
                setSuccess(true)
                setTimeout(() => setSuccess(false), 3000)
            } else if (resMsg && resMsg !== '{}' && !resMsg.includes('Server responded with status')) {
                toast.error(resMsg, { autoClose: 2500 })
            } else {
                toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
            }
        } catch (error) {
            console.error('Error subscribing:', error)
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="flex gap-2">
                <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={loading}
                    className="flex-1 min-w-0 px-4 py-2.5 rounded-xl text-sm transition-all"
                    style={{
                        background: 'rgba(140,88,38,0.08)',
                        border: '1px solid rgba(140,88,38,0.15)',
                        color: C.BROWN,
                        outline: 'none',
                    }}
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:-translate-y-0.5 disabled:opacity-50 flex-shrink-0"
                    style={{ background: C.PRIMARY, color: C.BROWN }}
                >
                    {loading ? '...' : success ? <CheckCircle size={15} /> : <Send size={15} />}
                </button>
            </div>
            <p className="text-[10px] mt-2" style={{ color: `${C.BROWN}88` }}>
                No spam. Unsubscribe anytime.
            </p>
        </form>
    )
}

const FooterNewsletter = (props: { companyId: string; domain?: string }) => (
    <RecaptchaProvider domain={props.domain || ''}>
        <FooterNewsletterInner {...props} />
    </RecaptchaProvider>
)

export default FooterNewsletter